import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Divider,
  LinearProgress,
  Typography
} from '@mui/material';
import api from '../../services/api';
import type { CompanyBudgetProgressItem } from '../../types/companyAccountEconomy';
import { useCompanyDashboardOptional } from './CompanyDashboardContext';

interface CompanyBudgetRollupCardProps {
  companyId: string;
}

export default function CompanyBudgetRollupCard({ companyId }: CompanyBudgetRollupCardProps) {
  const dashboard = useCompanyDashboardOptional();
  const economyVisible = dashboard?.accessGranted === true;

  const [items, setItems] = useState<CompanyBudgetProgressItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!economyVisible) {
      setItems([]);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const res = await api.getCompanyBudgetProgress(companyId);
      if (res.success) {
        setItems((res.data ?? []) as CompanyBudgetProgressItem[]);
      } else {
        setItems([]);
      }
    } catch (err: unknown) {
      const status =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { status?: number } }).response?.status
          : undefined;
      if (status === 403) {
        setItems([]);
        return;
      }
      setError('Failed to load budget rollup');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, economyVisible]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = useMemo(() => {
    const withBudget = items.length;
    const over = items.filter((i) => i.percent > 100).length;
    const types = new Set(items.map((i) => i.type));
    const currencies = new Set(items.map((i) => i.currencyCode ?? ''));
    const canSum = withBudget > 0 && types.size === 1 && currencies.size === 1;

    let totalRegistered = 0;
    let totalTarget = 0;
    if (canSum) {
      for (const item of items) {
        totalRegistered += item.registered;
        totalTarget += item.target;
      }
    }

    return { withBudget, over, canSum, totalRegistered, totalTarget, type: items[0]?.type ?? null };
  }, [items]);

  if (!economyVisible) {
    return null;
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Budget rollup
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Current evaluation periods across accounts
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {loading ? (
          <LinearProgress />
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : summary.withBudget === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No accounts with an active budget period.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Accounts with active budget
              </Typography>
              <Typography variant="body1">{summary.withBudget}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Over target
              </Typography>
              <Typography
                variant="body1"
                color={summary.over > 0 ? 'error.main' : 'text.primary'}
              >
                {summary.over}
              </Typography>
            </Box>
            {summary.canSum && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Combined registered / target
                </Typography>
                <Typography variant="body1">
                  {summary.totalRegistered.toFixed(2)} / {summary.totalTarget.toFixed(2)}
                </Typography>
                {summary.totalTarget > 0 && (
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, (summary.totalRegistered / summary.totalTarget) * 100)}
                    color={
                      summary.totalRegistered / summary.totalTarget > 1 ? 'error' : 'primary'
                    }
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
